import { useCallback, useMemo, useState } from "react";
import { Chart } from "primereact/chart";
import { RTU } from "../../types/RTU";
import type { DistributionViewMode } from "../../hooks/usePersistedDistributionView";
import PopupFirmwareVersion from "./PopupFirmwareVersion";
import { normalizeSoftwareVersion } from "./dashboardVersionNormalization";

const ROW_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#C9CBCF",
];

interface FirmwareCount {
  [key: string]: number;
}

interface Props {
  rtus: RTU[];
  viewMode: DistributionViewMode;
}

const toPercent = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 1000) / 10 : 0;

const FirmwareVersionPieChart: React.FC<Props> = ({ rtus, viewMode }) => {
  const [visible, setVisible] = useState(false);
  const [selectedRTUs, setSelectedRTUs] = useState<RTU[]>([]);

  const { rows, topFirmwareData, topVersions, total } = useMemo(() => {
    const firmwareData = rtus.reduce((acc: FirmwareCount, rtu) => {
      const version = normalizeSoftwareVersion(rtu.deploymentVersion);
      acc[version] = (acc[version] || 0) + 1;
      return acc;
    }, {} as FirmwareCount);

    const sortedFirmwareData = Object.entries(firmwareData).sort(
      (a, b) => b[1] - a[1]
    );
    const top = sortedFirmwareData.slice(0, 5);
    const topVersions = top.map(([version]) => version);
    const otherCount = sortedFirmwareData
      .slice(5)
      .reduce((acc, [, count]) => acc + count, 0);

    if (otherCount > 0) {
      top.push(["Other", otherCount]);
    }

    const t = rtus.length;
    const list = top.map(([version, count], index) => ({
      key: version,
      version,
      count,
      percent: toPercent(count, t),
      color: ROW_COLORS[Math.min(index, ROW_COLORS.length - 1)]!,
    }));

    return { rows: list, topFirmwareData: top, topVersions, total: t };
  }, [rtus]);

  const openForVersion = useCallback(
    (version: string) => {
      let relatedRTUs: RTU[];

      if (version === "Other") {
        relatedRTUs = rtus.filter(
          (rtu) =>
            !topVersions.includes(normalizeSoftwareVersion(rtu.deploymentVersion))
        );
      } else {
        relatedRTUs = rtus.filter(
          (rtu) => normalizeSoftwareVersion(rtu.deploymentVersion) === version
        );
      }
      setSelectedRTUs(relatedRTUs);
      setVisible(true);
    },
    [rtus, topVersions]
  );

  const closePopup = () => {
    setVisible(false);
  };

  /** Same structure as pre–lane G (`9e9fb99`): Prime `Chart` type `pie` + onClick chartElements. */
  const pieChartData = useMemo(
    () => ({
      labels: topFirmwareData.map(([version]) => version),
      datasets: [
        {
          data: topFirmwareData.map(([, count]) => count),
          backgroundColor: [
            "#FF6384",
            "#36A2EB",
            "#FFCE56",
            "#4BC0C0",
            "#9966FF",
            "#C9CBCF",
          ],
        },
      ],
    }),
    [topFirmwareData]
  );

  const pieChartOptions = useMemo(
    () => ({
      onClick: (event: MouseEvent, chartElements: { index: number }[]) => {
        if (chartElements.length > 0) {
          const index = chartElements[0]!.index;
          const version = topFirmwareData[index]?.[0];
          if (version !== undefined) {
            openForVersion(version);
          }
        }
      },
    }),
    [topFirmwareData, openForVersion]
  );

  if (rows.length === 0) {
    return (
      <div
        className="dashboard-distribution-kpi dashboard-distribution-kpi--empty"
        role="status"
      >
        —
      </div>
    );
  }

  return (
    <>
      {viewMode === "pie" ? (
        <div className="dashboard-distribution-pie" id="dashboard-firmware-distribution-pie">
          <Chart
            key="dashboard-firmware-distribution-pie"
            type="pie"
            data={pieChartData}
            options={{ ...pieChartOptions, maintainAspectRatio: false }}
            style={{ cursor: "pointer", width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <div className="dashboard-distribution-kpi" role="list">
          {rows.map((row) => (
            <button
              key={row.key}
              type="button"
              className="dashboard-distribution-kpi__row"
              onClick={() => openForVersion(row.version)}
              aria-label={row.version}
            >
              <div className="dashboard-distribution-kpi__row-top">
                <span className="dashboard-distribution-kpi__label" title={row.version}>
                  {row.version}
                </span>
                <span className="dashboard-distribution-kpi__pct">
                  {row.percent}%
                </span>
              </div>
              <div className="dashboard-distribution-kpi__bar-track">
                <span
                  className="dashboard-distribution-kpi__bar"
                  style={{
                    width: `${Math.min(100, row.percent)}%`,
                    background: row.color,
                  }}
                />
              </div>
              <span className="dashboard-distribution-kpi__count">
                {row.count} / {total}
              </span>
            </button>
          ))}
        </div>
      )}
      {visible && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 999,
          }}
        >
          <PopupFirmwareVersion rtus={selectedRTUs} onClose={closePopup} />
        </div>
      )}
    </>
  );
};

export default FirmwareVersionPieChart;
